import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { Button, Card, CheckBox, Message, Spinner } from '@iwoca/orion';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { TApplicationFormData } from 'lendingApi/applications.types';
import { TGetStateResponseRoles } from 'lendingApi/lapiOverrides.types';
import {
  useGetIntroducerApplication,
  useGetStateQuery,
} from 'lendingApi/lendingApiHooks';
import { keysIn } from 'lodash';
import { FormProvider, FormState, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { TImperativeHandleSignup } from 'types';
import { getCookie } from 'utils/cookieHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { ClientDetails } from '../ClientDetails/ClientDetails';
import { CompanyDetails } from '../CompanyDetails/CompanyDetails';
import { DevTimeSaver } from '../DevTimeSaver/DevTimeSaver';
import { useRouterApplicationId } from '../hooks/useRouterApplicationId';
import { useSignupData } from '../hooks/useSignupData';
import { useSignupStep } from '../hooks/useSignupStep';
import { LoanApplication } from '../LoanApplication/LoanApplication';
import styles from './SignupForm.module.css';
import { schema } from './SignUpForm.schema';

export type TResidentialStatus =
  | 'tenant'
  | 'owner_with_mortgage'
  | 'owner_no_mortgage'
  | 'rent_free'
  | undefined;
export type TAddressType = {
  flat_name?: string | undefined;
  house_name?: string | undefined;
  house_number?: string | undefined;
  postcode?: string | undefined;
  equifax_token?: string | undefined;
  street_line_1?: string | undefined;
  street_line_2?: string | undefined;
  town?: string | undefined;
  residential_status?: TResidentialStatus;
};
export type TClientDetailsType = {
  firstName: string;
  lastName: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  phoneNumber: string;
  secondaryPhoneNumber?: string;
  email: string;
  residentialAddresses: TAddressType[];
  privacyPolicyAgreement: boolean;
  roles: TGetStateResponseRoles;
  uid?: string;
};

export type TSignupFormData = {
  // Company information
  last12MonthsTurnover?: string;
  companyName?: string;
  companyNumber?: string;
  companyType?: GetCompanySearchResponse['data'][number]['company_type'];
  tradingStartDate?: string;
  startDateMonth?: string;
  startDateYear?: string;
  tradingName?: string;
  website?: string;
  isVatRegistered?: string;
  applicant: TClientDetailsType;
  guarantors: TClientDetailsType[];
} & TApplicationFormData; // Client details information

type TProps = {
  setSaveForLaterLoading?: (data: boolean) => void;
};

export const SignupForm = forwardRef<TImperativeHandleSignup, TProps>(
  ({ setSaveForLaterLoading }, ref) => {
    const navigate = useNavigate();
    const { trackEvent } = useAmplitude();
    const { updateLatestAllowedStep } = useSignupStep();
    const { stateKey, applicationData, stateData, updateSignupData } =
      useSignupData();
    const { checkIntroducerPermission } = useIntroducerContext();
    const isDisabled = !checkIntroducerPermission('can_create_applications');

    const applicationId = useRouterApplicationId();
    const { state, stateStatus } = useGetStateQuery();
    const { application, applicationStatus } = useGetIntroducerApplication();
    const stateHasLoaded = state && stateStatus === 'success';
    const applicationHasLoaded = application && applicationStatus === 'success';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const developer = getCookie('devTimeSaver');
    const defaultValues = {
      // Company values
      companyName: stateData.companyName,
      companyNumber: stateData.companyNumber,
      companyType: stateData.companyType,
      tradingName: stateData.tradingName,
      website: stateData.website,
      startDateMonth: stateData.startDateMonth,
      startDateYear: stateData.startDateYear,
      last12MonthsTurnover: stateData.last12MonthsTurnover,
      isVatRegistered: stateData.isVatRegistered,
      // Client values
      applicant: stateData.applicant,
      guarantors: stateData.guarantors,
      // Loan application values
      amount: applicationData.amount,
      loanDuration: applicationData.loanDuration,
      loanPurpose: applicationData.loanPurpose,
      loanPurposeDetails: applicationData.loanPurposeDetails,
    };

    const methods = useForm<TSignupFormData>({
      resolver: yupResolver(schema),
      defaultValues: defaultValues,
    });

    useEffect(() => {
      if (applicationHasLoaded && stateHasLoaded) {
        methods.reset(defaultValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationHasLoaded, stateHasLoaded]);

    // React hook that allows this referenced component to return a single function instead of the entire return value of the element
    useImperativeHandle(
      ref,
      () => {
        return {
          async saveFormData() {
            setSaveForLaterLoading?.(true);
            await updateSignupData(methods.getValues());
            navigate(PATH_NAMES.home());
            setSaveForLaterLoading?.(false);
          },
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [methods, updateSignupData],
    );

    const onSubmit = async (values: TSignupFormData) => {
      setIsSubmitting(true);

      if (!stateKey) {
        return;
      }

      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['offers'],
      });

      trackEvent(AMPLITUDE_EVENTS.newApplicationSubmitted, {
        'State key': stateKey,
      });

      const { updatedState } = await updateSignupData(values);

      await updateLatestAllowedStep('new/productOffers', updatedState);
      navigate(PATH_NAMES.newApplicationProductOffers({ applicationId }));
      setIsSubmitting(false);
    };

    const trackError = (error?: string) => {
      if (!error) return;

      trackEvent(AMPLITUDE_EVENTS.newApplicationSubmissionErrored, {
        'Error message': error,
      });

      datadogLogs.logger.error('Failed to submit application', {
        createError: error,
        stateKey,
      });
    };

    const onError = (error: FormState<TSignupFormData>['errors']) => {
      if (!error) {
        return;
      }
      const errorKeys = keysIn(error) as (keyof TSignupFormData)[];

      // Breaking down the error object into the individual messages
      // based on our form schema
      errorKeys.forEach((errorKey) => {
        if (errorKey === 'guarantors') {
          (error.guarantors || [])?.forEach?.((person) => {
            const personKeys = keysIn(person) as (keyof TClientDetailsType)[];
            if (person) {
              personKeys.forEach((personKey) => {
                if (personKey === 'residentialAddresses') {
                  person.residentialAddresses?.forEach?.((address) => {
                    const addressKeys = keysIn(
                      address,
                    ) as (keyof TAddressType)[];
                    if (address) {
                      addressKeys.forEach((addressKey) => {
                        const errorMessage = address[addressKey]?.message;
                        trackError(errorMessage);
                      });
                    }
                  });
                }
                const errorMessage = person[personKey]?.message;
                trackError(errorMessage);
              });
            }
          });
        }
        if (errorKey === 'applicant') {
          const personKeys = keysIn(
            error.applicant,
          ) as (keyof TClientDetailsType)[];

          if (error.applicant) {
            personKeys.forEach((personKey) => {
              if (personKey === 'residentialAddresses') {
                error.applicant?.residentialAddresses?.forEach?.((address) => {
                  const addressKeys = keysIn(address) as (keyof TAddressType)[];
                  if (address) {
                    addressKeys.forEach((addressKey) => {
                      const errorMessage = address[addressKey]?.message;
                      trackError(errorMessage);
                    });
                  }
                });
              }
              const errorMessage = error.applicant?.[personKey]?.message;
              trackError(errorMessage);
            });
          }
        } else {
          trackError(error[errorKey]?.message);
        }
      });
    };

    if (!stateHasLoaded || !applicationHasLoaded) {
      return (
        <div className={styles.container}>
          <Card className={styles.form}>
            <Spinner
              className={styles.spinner}
              size="large"
              data-testid="loading-spinner"
            />
          </Card>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <FormProvider {...methods}>
          <form
            className={styles.form}
            onSubmit={methods.handleSubmit(onSubmit, onError)}
          >
            <CompanyDetails />
            <ClientDetails />
            <LoanApplication />
            <div className={styles.privacyPolicyAgreement}>
              <p>
                We’ll use your client's (and other directors or beneficial
                owners of their company, as well as anyone providing a personal
                guarantee) personal information in a few ways:
              </p>
              <ul className={styles.personalInfoList}>
                <li>
                  <strong>
                    We’ll share it with credit reference agencies to run soft
                    credit and identity checks
                  </strong>{' '}
                  - this doesn’t affect your client's (or others') personal
                  credit score.
                </li>
                <li>
                  <strong>We’ll share it with fraud prevention agencies</strong>{' '}
                  - if we do spot any fraud, your client or others could be
                  refused services, finance, or employment.
                </li>
                <li>
                  <strong>We might record your client’s calls with us.</strong>
                </li>
              </ul>
              <p>
                Remember to make sure your client and the others (directors,
                beneficial owners or anyone providing a personal guarantee) are
                aware of this before you do. Please also provide them with a
                copy of our{' '}
                <a
                  href="https://www.iwoca.co.uk/introducer-privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy policy
                </a>
                . The data controller is iwoca Ltd.
              </p>
            </div>
            <div className={styles.footerCtas}>
              <CheckBox
                className={styles.privacyPolicyCheckbox}
                {...methods.register('applicant.privacyPolicyAgreement')}
                label="I’ve read and agreed to all terms & conditions"
                data-testid="privacyPolicyAgreement"
                disabled={isDisabled}
              />
              {methods.formState.errors.applicant?.privacyPolicyAgreement
                ?.message && (
                <Message
                  message={
                    methods.formState.errors.applicant?.privacyPolicyAgreement
                      ?.message
                  }
                  type="error"
                />
              )}
              <div className={styles.signupFormSubmit}>
                <Button
                  variant="primary"
                  size="large"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isDisabled}
                >
                  Submit application
                </Button>
              </div>
            </div>
          </form>
          {developer && <DevTimeSaver />}
        </FormProvider>
      </div>
    );
  },
);

SignupForm.displayName = 'SignupForm';
